import React, { memo, HTMLProps, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { PLACE_HOLDER } from 'src/constants';
import { ImgUtils, Logger } from 'src/utils';
import { Element } from '../base';

interface IProps extends HTMLProps<HTMLImageElement> {
    src: string;
    alt?: string;
    size?: number;
    rounded?: boolean;
    className?: string;
    renderAs?: string;
    children?: React.ReactNode;
    defaultSrc?: string;
}

const Image = React.forwardRef(
    (
        {
            src = '',
            defaultSrc = '',
            alt = '',
            size,
            rounded,
            className,
            children,
            renderAs = 'figure',
            width,
            height,
            ...rest
        }: IProps,
        ref,
    ) => {
        const [error, setError] = useState<boolean>(false);
        const imgRef = useRef<HTMLImageElement>(null);
        const handleError = () => {
            if (error) return;

            setError(true);

            Logger.errorImage('Error loadImage fail|src:', {
                src,
                alt,
            });
        };

        useEffect(() => {
            const img = imgRef.current;

            if (!img) return;

            if (img.complete && img.naturalWidth === 0) {
                handleError();
            }
        }, [src]);

        return (
            <Element
                {...rest}
                domRef={ref}
                renderAs={renderAs}
                className={classnames(
                    'image',
                    {
                        'is-rounded': rounded,
                    },
                    className,
                )}
            >
                {src && (
                    <picture>
                        <source
                            srcSet={
                                error
                                    ? defaultSrc || PLACE_HOLDER.IMAGE
                                    : ImgUtils.convertToAvif(src)
                            }
                            type="image/avif"
                        />
                        <source
                            srcSet={
                                error
                                    ? defaultSrc || PLACE_HOLDER.IMAGE
                                    : ImgUtils.convertToWebp(src)
                            }
                            type="image/webp"
                        />
                        <img
                            ref={imgRef}
                            src={error ? defaultSrc || PLACE_HOLDER.IMAGE : src}
                            alt={alt}
                            width={width}
                            height={height}
                            onError={handleError}
                        />
                    </picture>
                )}
            </Element>
        );
    },
);

export default memo(Image);
